import { el, clear } from '../lib/dom.js';
import { settings } from '../store/settings.js';
import { loadSnapshot } from '../data/snapshot.js';
import { prettify } from '../data/pokedex-resolver.js';
import { TYPES, multiplier, offensiveSummary, defensiveMatchups } from '../data/type-chart.js';
import { TypeChip } from '../components/TypeChip.js';
import { typeHex } from '../lib/type-color.js';

const ABBR = {
  normal: 'NOR',
  fire: 'FIR',
  water: 'WAT',
  electric: 'ELE',
  grass: 'GRA',
  ice: 'ICE',
  fighting: 'FIG',
  poison: 'POI',
  ground: 'GRD',
  flying: 'FLY',
  psychic: 'PSY',
  bug: 'BUG',
  rock: 'ROC',
  ghost: 'GHO',
  dragon: 'DRA',
  dark: 'DRK',
  steel: 'STE',
  fairy: 'FAI',
};
const cellText = (m) => (m === 0 ? '0' : m === 0.5 ? '½' : m === 2 ? '2' : '');
const cellClass = (m) => (m === 0 ? 'm0' : m === 0.5 ? 'm05' : m === 2 ? 'm2' : 'm1');

export async function TypeChartView() {
  const view = el('section', { class: 'view lookup tchartview' });
  const snap = await loadSnapshot();
  const st = settings.get();
  const vg = snap.versionGroupByKey.get(st.versionGroup);
  const gen = vg ? vg.generation : 9;

  // Fairy is Gen 6+, Steel/Dark are Gen 2+.
  const types = TYPES.filter((t) => {
    if (t === 'fairy' && gen < 6) return false;
    if ((t === 'steel' || t === 'dark') && gen < 2) return false;
    return true;
  });

  let selected = null; // a type name, or null
  const grid = el('div', { class: 'tchart' });
  const summary = el('div', { class: 'tchart__summary' });

  function select(t) {
    selected = selected === t ? null : t;
    paint();
  }

  function paint() {
    grid.style.setProperty('--n', String(types.length));
    clear(grid);

    // corner
    grid.append(el('span', { class: 'tchart__corner' }, 'ATK→\nDEF↓'));
    // column headers (defending)
    for (const def of types) {
      grid.append(
        el(
          'button',
          {
            type: 'button',
            class: `tchart__col${selected === def ? ' is-sel' : ''}`,
            style: `--tc:${typeHex(def)}`,
            title: prettify(def),
            onclick: () => select(def),
          },
          ABBR[def],
        ),
      );
    }
    // rows (attacking)
    for (const atk of types) {
      grid.append(
        el(
          'button',
          {
            type: 'button',
            class: `tchart__row${selected === atk ? ' is-sel' : ''}`,
            style: `--tc:${typeHex(atk)}`,
            title: prettify(atk),
            onclick: () => select(atk),
          },
          ABBR[atk],
        ),
      );
      for (const def of types) {
        const m = multiplier(atk, [def], gen);
        const hot = selected === atk || selected === def;
        grid.append(
          el('span', { class: `tchart__cell ${cellClass(m)}${hot ? ' is-hot' : ''}` }, cellText(m)),
        );
      }
    }

    clear(summary);
    if (!selected) {
      summary.append(
        el(
          'p',
          { class: 'detail__muted' },
          'Tap a type on the edge to see everything it hits and everything that hits it.',
        ),
      );
      return;
    }
    const inEra = (list) => (list || []).filter((t) => types.includes(t));
    const off = offensiveSummary([selected], gen);
    const def = defensiveMatchups([selected], gen);
    const chipRow = (label, list) =>
      list.length
        ? el(
            'div',
            { class: 'tchart__srow' },
            el('span', { class: 'tchart__slabel' }, label),
            el('span', { class: 'matchups__types' }, ...list.map(TypeChip)),
          )
        : null;
    summary.append(
      el('h3', { class: 'ppanel__sub' }, `${prettify(selected)} — attacking`),
      chipRow('Super effective vs', inEra(off.strong)),
      chipRow('Not very effective / no effect vs', inEra(off.walls)),
      el('h3', { class: 'ppanel__sub' }, `${prettify(selected)} — defending`),
      chipRow('Weak to', inEra(def['2'])),
      chipRow('Resists', inEra(def['0.5'])),
      chipRow('Immune to', inEra(def['0'])),
    );
  }
  paint();

  clear(view).append(
    el('nav', { class: 'lookup__nav' }, el('a', { class: 'link', href: '#/team' }, '‹ Team')),
    el(
      'header',
      { class: 'view__header' },
      el('h1', {}, 'Type chart'),
      el(
        'p',
        {},
        st.versionGroup === 'all'
          ? 'Modern rules (Gen 6+).'
          : `${prettify(st.versionGroup)} — Gen ${gen} rules${
              gen < 6 ? ', no Fairy' : ''
            }${gen < 2 ? ', no Steel/Dark' : ''}.`,
      ),
    ),
    el(
      'div',
      { class: 'tchart__legend' },
      el('span', { class: 'tchart__cell m2' }, '2'),
      ' super effective  ',
      el('span', { class: 'tchart__cell m05' }, '½'),
      ' resisted  ',
      el('span', { class: 'tchart__cell m0' }, '0'),
      ' no effect',
    ),
    el('div', { class: 'tchart__scroll' }, grid),
    summary,
  );
  return view;
}
